import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Loader2,
  PhoneCall,
  UserRound,
} from 'lucide-react';
import packageJson from '../package.json';
import './styles.css';

const initialForm = {
  name: '',
  phone: '',
  callPreference: '',
  callDate: '',
  callTimeSlot: '',
  timeZone: getBrowserTimeZone(),
  website: '',
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const EASTERN_TIME_ZONE = 'America/New_York';
const BUSINESS_START_HOUR = 9;
const BUSINESS_END_HOUR = 17;
const APP_VERSION = packageJson.version;
const today = startOfDay(new Date());

function App() {
  const [form, setForm] = React.useState(initialForm);
  const [status, setStatus] = React.useState({ type: 'idle', message: '' });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  function updateField(event) {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function updateCallPreference(value) {
    setForm((current) => ({
      ...current,
      callPreference: value,
      callDate: value === 'specific' ? current.callDate : '',
      callTimeSlot: value === 'specific' ? current.callTimeSlot : '',
    }));
  }

  function updateCallDate(value) {
    setForm((current) => ({ ...current, callDate: value, callTimeSlot: '' }));
  }

  function updateCallTimeSlot(value) {
    setForm((current) => ({ ...current, callTimeSlot: value }));
  }

  function resetForm() {
    setForm({ ...initialForm, timeZone: getBrowserTimeZone() });
    setStatus({ type: 'idle', message: '' });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!form.callPreference) {
      setStatus({ type: 'error', message: 'Please choose when you would like us to call.' });
      return;
    }

    if (form.callPreference === 'specific' && !form.callDate) {
      setStatus({ type: 'error', message: 'Please choose an available weekday for your call.' });
      return;
    }

    if (form.callPreference === 'specific' && !form.callTimeSlot) {
      setStatus({ type: 'error', message: 'Please choose an available time for your call.' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: 'idle', message: '' });

    try {
      const response = await fetch('/api/request-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Unable to send your request.');
      }

      setForm({ ...initialForm, timeZone: getBrowserTimeZone() });
      setStatus({ type: 'success', message: data.message });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="app-shell">
      <section className="app-container">
        <div className="ambient ambient-left" aria-hidden="true" />
        <div className="ambient ambient-right" aria-hidden="true" />

        {status.type === 'success' ? (
          <SuccessView message={status.message} onReset={resetForm} />
        ) : (
          <form
            onSubmit={handleSubmit}
            className="app-card"
          >
            <div className="form-hero">
              <div className="hero-icon">
                <PhoneCall aria-hidden="true" size={18} />
              </div>
              <p className="eyebrow">
                BarMasters Concierge
              </p>
              <h1>
                Request a Phone Call
              </h1>
              <p className="hero-copy">
                Tell us who to call and when. We will follow up with a real person during weekday business hours.
              </p>
            </div>

            <div className="form-grid">
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={updateField}
                tabIndex={-1}
                autoComplete="off"
                className="honeypot"
                aria-hidden="true"
              />

              <Field label="Name" htmlFor="name" icon={<UserRound aria-hidden="true" size={15} />}>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={updateField}
                  autoComplete="name"
                  required
                  minLength={2}
                  placeholder="Jane Smith"
                  className="input"
                />
              </Field>

              <Field label="Phone Number" htmlFor="phone" icon={<PhoneCall aria-hidden="true" size={15} />}>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={updateField}
                  autoComplete="tel"
                  required
                  placeholder="(555) 123-4567"
                  className="input"
                />
              </Field>

              <div className="full-row">
                <fieldset>
                  <legend className="field-label">
                    <span className="field-icon">
                      <Clock3 aria-hidden="true" size={15} />
                    </span>
                    When should we call?
                    <span className="required-mark" aria-hidden="true">
                      *
                    </span>
                  </legend>
                  <div className="preference-grid">
                    <PreferenceCard
                      title="As soon as possible"
                      description="Have someone reach out at the next available opening."
                      icon={<PhoneCall aria-hidden="true" size={16} />}
                      selected={form.callPreference === 'asap'}
                      onClick={() => updateCallPreference('asap')}
                    />
                    <PreferenceCard
                      title="Schedule a weekday"
                      description="Choose an exact date from the calendar."
                      icon={<CalendarDays aria-hidden="true" size={16} />}
                      selected={form.callPreference === 'specific'}
                      onClick={() => updateCallPreference('specific')}
                    />
                  </div>
                </fieldset>
              </div>

              {form.callPreference === 'specific' && (
                <div className="full-row">
                  <DatePicker
                    value={form.callDate}
                    timeSlot={form.callTimeSlot}
                    timeZone={form.timeZone}
                    onDateChange={updateCallDate}
                    onTimeSlotChange={updateCallTimeSlot}
                  />
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="submit-button"
            >
              {isSubmitting ? (
                <Loader2 aria-hidden="true" size={15} className="animate-spin" />
              ) : (
                <CheckCircle2 aria-hidden="true" size={15} />
              )}
              {isSubmitting ? 'Sending request...' : 'Request Phone Call'}
            </button>

            <StatusMessage status={status} />
          </form>
        )}

        <p className="app-version">v{APP_VERSION}</p>
      </section>
    </main>
  );
}

function DatePicker({ value, timeSlot, timeZone, onDateChange, onTimeSlotChange }) {
  const selectedDate = value ? parseDateOnly(value) : null;
  const [visibleMonth, setVisibleMonth] = React.useState(() =>
    selectedDate ? startOfMonth(selectedDate) : startOfMonth(today),
  );

  React.useEffect(() => {
    if (selectedDate) {
      setVisibleMonth(startOfMonth(selectedDate));
    }
  }, [value]);

  const days = React.useMemo(() => buildCalendarDays(visibleMonth), [visibleMonth]);
  const timeSlots = React.useMemo(() => buildBusinessHourSlots(value, timeZone), [value, timeZone]);
  const canGoBack = visibleMonth > startOfMonth(today);

  return (
    <div>
      <Field label="Preferred call date" htmlFor="callDate" icon={<CalendarDays aria-hidden="true" size={15} />}>
        <div id="callDate" className="selected-date">
          <span className={value ? 'selected-date-value' : 'selected-date-placeholder'}>
            {selectedDate
              ? selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
              : 'Choose an available weekday'}
          </span>
          <CalendarDays aria-hidden="true" size={15} className="text-brand-600" />
        </div>
      </Field>

      <div className="date-card">
        <div className="date-card-header">
          <div>
            <p className="date-title">Pick a weekday</p>
            <p className="date-help">Weekend dates are unavailable.</p>
          </div>
          <div className="date-nav">
            <button
              type="button"
              onClick={() => setVisibleMonth((current) => addMonths(current, -1))}
              disabled={!canGoBack}
              className="icon-button"
              aria-label="Previous month"
            >
              <ChevronLeft aria-hidden="true" size={14} />
            </button>
            <button
              type="button"
              onClick={() => setVisibleMonth((current) => addMonths(current, 1))}
              className="icon-button"
              aria-label="Next month"
            >
              <ChevronRight aria-hidden="true" size={14} />
            </button>
          </div>
        </div>

        <div className="month-label">
          {visibleMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>

        <div className="calendar-grid">
          {WEEKDAYS.map((day) => (
            <div key={day} className="weekday-label">
              {day}
            </div>
          ))}

          {days.map((day) => {
            const dateOnly = toDateOnly(day);
            const isSelected = value === dateOnly;
            const isOutsideMonth = day.getMonth() !== visibleMonth.getMonth();
            const disabled = isUnavailable(day) || isOutsideMonth;

            return (
              <button
                key={dateOnly}
                type="button"
                disabled={disabled}
                onClick={() => onDateChange(dateOnly)}
                className={[
                  'calendar-day',
                  isSelected ? 'calendar-day-selected' : '',
                  isOutsideMonth ? 'calendar-day-outside' : '',
                ].join(' ')}
                aria-pressed={isSelected}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      {value && (
        <div className="time-card">
          <div className="time-card-header">
            <p className="date-title">Pick a local time</p>
            <p className="date-help">Times are shown in your timezone and match our Eastern business hours.</p>
          </div>

          <div className="time-slot-grid">
            {timeSlots.map((slot) => {
              const isSelected = timeSlot === slot.value;

              return (
                <button
                  key={slot.value}
                  type="button"
                  onClick={() => onTimeSlotChange(slot.value)}
                  className={`time-slot${isSelected ? ' time-slot-selected' : ''}`}
                  aria-pressed={isSelected}
                >
                  <span>{slot.label}</span>
                  {slot.dateLabel && <span className="time-slot-date">{slot.dateLabel}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PreferenceCard({ title, description, icon, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`preference-card${selected ? ' preference-card-selected' : ''}`}
      aria-pressed={selected}
    >
      <span className="preference-icon">
        {icon}
      </span>
      <span className="preference-title">{title}</span>
      <span className="preference-description">{description}</span>
    </button>
  );
}

function Field({ label, htmlFor, icon, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="field-label">
        <span className="field-icon">{icon}</span>
        {label}
        <span className="required-mark" aria-hidden="true">
          *
        </span>
      </label>
      {children}
    </div>
  );
}

function SuccessView({ message, onReset }) {
  const displayMessage = message ? message.replace(/\.$/, '!') : 'Your phone call request was sent!';

  return (
    <section className="app-card success-card" role="status" aria-live="polite">
      <div className="success-glow" aria-hidden="true" />
      <div className="success-icon-wrap">
        <span className="success-pulse" aria-hidden="true" />
        <div className="success-icon">
          <CheckCircle2 aria-hidden="true" size={34} strokeWidth={2.4} />
        </div>
      </div>
      <p className="eyebrow">BarMasters Concierge</p>
      <h1 className="success-title">Request Received!</h1>
      <p className="success-message">{displayMessage}</p>
      <p className="success-copy">
        A real person on our team now has your phone request and will follow up during weekday business hours.
      </p>
      <button type="button" className="success-reset-button" onClick={onReset}>
        <PhoneCall aria-hidden="true" size={15} />
        Submit Another Request
      </button>
    </section>
  );
}

function StatusMessage({ status }) {
  if (status.type === 'idle') return null;

  const className =
    status.type === 'success'
      ? 'border-brand-100 bg-brand-50 text-brand-700'
      : 'border-red-100 bg-red-50 text-red-700';

  return (
    <p className={`mt-5 rounded-md border px-4 py-3 text-center text-sm font-medium ${className}`} role="status">
      {status.message}
    </p>
  );
}

function buildCalendarDays(monthDate) {
  const firstDay = startOfMonth(monthDate);
  const start = new Date(firstDay);
  start.setDate(firstDay.getDate() - firstDay.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

function isUnavailable(date) {
  return date < today || date.getDay() === 0 || date.getDay() === 6;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, amount) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function buildBusinessHourSlots(dateOnly, timeZone) {
  if (!dateOnly) return [];

  return Array.from({ length: BUSINESS_END_HOUR - BUSINESS_START_HOUR }, (_, index) => {
    const start = zonedTimeToUtc(dateOnly, BUSINESS_START_HOUR + index, EASTERN_TIME_ZONE);
    const end = zonedTimeToUtc(dateOnly, BUSINESS_START_HOUR + index + 1, EASTERN_TIME_ZONE);

    return {
      value: start.toISOString(),
      label: formatLocalTimeSlot(start, end, timeZone),
      dateLabel: getLocalDateNote(start, end, dateOnly, timeZone),
    };
  });
}

function formatLocalTimeSlot(start, end, timeZone) {
  const startTime = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(start);
  const endTime = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
    timeZoneName: 'short',
  }).format(end);

  return `${startTime} - ${endTime}`;
}

function getLocalDateNote(start, end, selectedDateOnly, timeZone) {
  const localStartDate = toDateOnlyInTimeZone(start, timeZone);
  const localEndDate = toDateOnlyInTimeZone(end, timeZone);

  if (localStartDate === selectedDateOnly && localEndDate === selectedDateOnly) {
    return '';
  }

  if (localStartDate === localEndDate) {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone,
    }).format(start);
  }

  const startLabel = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone,
  }).format(start);
  const endLabel = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone,
  }).format(end);

  return `${startLabel} - ${endLabel}`;
}

function zonedTimeToUtc(dateOnly, hour, timeZone) {
  const [year, month, day] = dateOnly.split('-').map(Number);
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, 0, 0));
  const zonedParts = getTimeZoneParts(utcGuess, timeZone);
  const zonedAsUtc = Date.UTC(
    zonedParts.year,
    zonedParts.month - 1,
    zonedParts.day,
    zonedParts.hour,
    zonedParts.minute,
    zonedParts.second,
  );
  const targetAsUtc = Date.UTC(year, month - 1, day, hour, 0, 0);

  return new Date(utcGuess.getTime() - (zonedAsUtc - targetAsUtc));
}

function getTimeZoneParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).formatToParts(date);

  return parts.reduce((current, part) => {
    if (part.type !== 'literal') {
      current[part.type] = Number(part.value);
    }

    return current;
  }, {});
}

function parseDateOnly(value) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function toDateOnlyInTimeZone(date, timeZone) {
  const parts = getTimeZoneParts(date, timeZone);
  return [
    parts.year,
    String(parts.month).padStart(2, '0'),
    String(parts.day).padStart(2, '0'),
  ].join('-');
}

function toDateOnly(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getBrowserTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
